import { useFormikContext } from 'formik';
import { useMemo, useState, useEffect } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { useTranslation } from '@packlink/translation-provider';
import { ProviderName, AmplitudeEvents } from '@packlink/metrics';
import { SidePanel, SlideInPosition, SlideInSize, SidePanelDefaultFooter, SidePanelDefaultHeader, Button, ButtonVariant, Icon, IconSize } from '@shipengine/giger';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { useSendMetrics } from '../../MetricsProvider/useSendMetrics.js';
import { useFiltersValues } from '../../../hooks/useFiltersValues.js';
import { getLengthValues, getSidePanelFilteredServices } from '../../../utils/Filters.js';
import { MAX_CARRIERS_FILTERS } from '../../../constants/filters.js';
import { FilterSection } from '../FilterSection/FilterSection.js';
import { getMetrics } from '../../../utils/PayloadFilters.js';

const FiltersForm = _ref => {
  let {
    containerId,
    filters,
    isSidePanelOpen,
    services,
    selectedFilters,
    toggleSidePanel
  } = _ref;
  const {
    t
  } = useTranslation();
  const sendMetrics = useSendMetrics();
  const {
    values,
    handleReset,
    handleSubmit,
    setValues
  } = useFormikContext();
  const clearValues = useFiltersValues(filters);
  const {
    carriers,
    collectionTypes,
    deliveryTypes,
    serviceDetails,
    transitHours,
    tagTypes
  } = filters;
  const totalActiveFilters = getLengthValues({
    ...values.carriers,
    ...values.collectionTypes,
    ...values.deliveryTypes,
    ...values.serviceDetails,
    ...values.transitHours,
    ...values.tagTypes
  });
  const totalServices = useMemo(() => {
    return getSidePanelFilteredServices(services, values).length || 0;
  }, [services, values]);
  const defaultVisibleCarrierFilters = carriers.slice(0, MAX_CARRIERS_FILTERS);
  const [visibleCarrierFilters, setVisibleCarrierFilters] = useState(defaultVisibleCarrierFilters);
  const [isMoreActive, setIsMoreActive] = useState(false);
  const isHiddenCarrierFilters = carriers.length > visibleCarrierFilters.length;
  const handleOnClick = () => {
    setVisibleCarrierFilters(isHiddenCarrierFilters ? carriers : defaultVisibleCarrierFilters);
    setIsMoreActive(!isMoreActive);
  };
  useEffect(() => {
    const carrierFilterResults = (selectedFilters === null || selectedFilters === void 0 ? void 0 : selectedFilters.carriers) && Object.values(selectedFilters.carriers) || [];
    const areRestCarriersSelected = carrierFilterResults.some((value, id) => value === true && id > MAX_CARRIERS_FILTERS - 1);
    if (isSidePanelOpen) {
      setIsMoreActive(areRestCarriersSelected);
      setVisibleCarrierFilters(areRestCarriersSelected ? carriers : defaultVisibleCarrierFilters);
    }
  }, [isSidePanelOpen]);
  const handleApplyFilters = () => {
    const {
      carriers,
      collectionTypes,
      deliveryTypes,
      serviceDetails,
      transitHours,
      tagTypes
    } = values;
    sendMetrics(getMetrics(carriers, collectionTypes, deliveryTypes, serviceDetails, transitHours, tagTypes));
    handleSubmit();
  };
  const handleClearFilters = () => {
    sendMetrics({
      providerName: ProviderName.AMPLITUDE,
      eventName: AmplitudeEvents.SERVICE_LIST_REMOVE_FILTERS
    });
    setValues(clearValues, false);
  };
  const onCloseSidePanel = () => {
    toggleSidePanel();
    handleReset();
    sendMetrics({
      providerName: ProviderName.AMPLITUDE,
      eventName: AmplitudeEvents.SERVICE_LIST_CLOSE_FILTERS
    });
  };
  const isFilterActive = filter => {
    return filter.length > 1;
  };
  return jsxs(SidePanel, {
    containerId: containerId,
    position: SlideInPosition.RIGHT,
    size: SlideInSize.REGULAR,
    isOpen: isSidePanelOpen,
    onBackdropClick: onCloseSidePanel,
    applyBackdropToBody: true,
    includeBackdrop: true,
    fullWidthOnMobile: true,
    footer: jsx(SidePanelDefaultFooter, {
      onClickCancel: handleClearFilters,
      onClickSubmit: handleApplyFilters,
      isCancelBtnDisabled: !totalActiveFilters,
      isSubmitBtnDisabled: !totalServices,
      cancelText: t('service-list.side-panel.clear-filters'),
      submitText: totalServices ? t('service-list.side-panel.see-results') + ` (${totalServices})` : t('service-list.side-panel.no-results')
    }),
    header: jsx(SidePanelDefaultHeader, {
      title: t('service-list.side-panel.header'),
      closeButtonTooltip: t('service-list.side-panel.close'),
      onClickClose: onCloseSidePanel
    }),
    children: [tagTypes.length > 0 && jsx(FilterSection, {
      filters: tagTypes,
      sectionName: "tagTypes",
      titleKey: "service-list.side-panel.tag-types"
    }), isFilterActive(carriers) && jsx(FilterSection, {
      filters: visibleCarrierFilters,
      sectionName: "carriers",
      titleKey: "service-list.side-panel.couriers",
      children: carriers.length > MAX_CARRIERS_FILTERS && jsxs(Button, {
        variant: ButtonVariant.TEXT,
        onClick: handleOnClick,
        "data-id": "button-side-more",
        children: [jsx(Icon, {
          name: isMoreActive ? IconNames.DASH : IconNames.PLUS,
          size: isMoreActive ? IconSize.SIZE_REGULAR : IconSize.SIZE_SMALL
        }), jsx("span", {
          children: t(isMoreActive ? 'service-list.button.less' : 'service-list.button.more')
        })]
      })
    }), isFilterActive(collectionTypes) && jsx(FilterSection, {
      filters: collectionTypes,
      sectionName: "collectionTypes",
      titleKey: "service-list.side-panel.collection-types"
    }), isFilterActive(deliveryTypes) && jsx(FilterSection, {
      filters: deliveryTypes,
      sectionName: "deliveryTypes",
      titleKey: "service-list.side-panel.delivery-types"
    }), isFilterActive(transitHours) && jsx(FilterSection, {
      filters: transitHours,
      sectionName: "transitHours",
      titleKey: "service-list.side-panel.transit-time"
    }), isFilterActive(serviceDetails) && jsx(FilterSection, {
      filters: serviceDetails,
      sectionName: "serviceDetails",
      titleKey: "service-list.side-panel.service-details"
    })]
  });
};
FiltersForm.displayName = 'FiltersForm';

export { FiltersForm, FiltersForm as default };
//# sourceMappingURL=FiltersForm.js.map
